/**
 * Telegram Mini App.
 *
 * The SDK script is loaded here and only here: telegram.org is not reachable
 * from everywhere, and a static <script> tag would block the PWA from loading.
 */
import type { ApiClient } from '@/api/client'
import type { Tokens } from '@/api/types'
import {
  LocationDenied,
  LocationUnavailable,
  type Coordinates,
  type InvoiceStatus,
  type LocationPermission,
  type Platform,
} from './types'

const SDK_URL = 'https://telegram.org/js/telegram-web-app.js'
const SDK_TIMEOUT_MS = 5000
const GEO_TIMEOUT_MS = 15000

declare global {
  interface Window {
    Telegram?: { WebApp?: any }
  }
}

export async function loadTelegramSdk(): Promise<any | null> {
  if (window.Telegram?.WebApp) return window.Telegram.WebApp

  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = SDK_URL
    script.async = true

    const done = (value: any | null) => {
      clearTimeout(timer)
      resolve(value)
    }
    const timer = setTimeout(() => done(window.Telegram?.WebApp ?? null), SDK_TIMEOUT_MS)

    script.onload = () => done(window.Telegram?.WebApp ?? null)
    script.onerror = () => done(null)
    document.head.appendChild(script)
  })
}

/** Telegram theme colours become CSS variables Tailwind reads. */
function applyTheme(webApp: any): void {
  const params = webApp.themeParams ?? {}
  const root = document.documentElement
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string') {
      root.style.setProperty(`--tg-theme-${key.replace(/_/g, '-')}`, value)
    }
  }
  root.dataset.platform = 'telegram'
  root.dataset.theme = webApp.colorScheme ?? 'light'
}

export function createTelegramPlatform(webApp: any): Platform {
  return {
    kind: 'telegram',

    async init() {
      webApp.ready?.()
      webApp.expand?.()
      webApp.disableVerticalSwipes?.()
      applyTheme(webApp)
      webApp.onEvent?.('themeChanged', () => applyTheme(webApp))
    },

    async authenticate(api: ApiClient): Promise<Tokens | null> {
      const initData: string = webApp.initData ?? ''
      if (!initData) return api.tokens
      const tokens = await api.loginWithTelegram(initData)
      api.setTokens(tokens)
      return tokens
    },

    async getLocation(): Promise<Coordinates> {
      const manager = webApp.LocationManager
      if (manager?.init) {
        const fromTelegram = await new Promise<Coordinates | null>((resolve) => {
          try {
            manager.init(() => {
              if (!manager.isLocationAvailable) {
                resolve(null)
                return
              }
              manager.getLocation((location: any) => {
                resolve(location ? { lat: location.latitude, lon: location.longitude } : null)
              })
            })
          } catch {
            resolve(null)
          }
        })
        if (fromTelegram) return fromTelegram
      }

      // Older clients, or a client that refused: the WebView still has the browser API
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new LocationUnavailable())
          return
        }
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
          (error) => reject(error.code === error.PERMISSION_DENIED ? new LocationDenied() : new LocationUnavailable()),
          { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 60000 },
        )
      })
    },

    /**
     * У мини-приложения собственное разрешение, и браузерный Permissions API
     * отвечал бы здесь про доступ WebView — то есть не про то. LocationManager
     * знает точно и ничего не спрашивает: init() только инициализирует, диалог
     * поднимает getLocation().
     */
    async readLocationPermission(): Promise<LocationPermission> {
      const manager = webApp.LocationManager
      if (!manager?.init) return 'unknown'

      return new Promise<LocationPermission>((resolve) => {
        try {
          manager.init(() => {
            if (!manager.isLocationAvailable) resolve('unknown')
            else if (manager.isAccessGranted) resolve('granted')
            else if (manager.isAccessRequested) resolve('denied')
            else resolve('prompt')
          })
        } catch {
          resolve('unknown')
        }
      })
    },

    // Telegram is the one place with a real revoke: the mini app has its own
    // location permission and its own screen for it
    openLocationSettings() {
      const manager = webApp.LocationManager
      if (!manager?.openSettings) return false
      try {
        manager.openSettings()
        return true
      } catch {
        return false
      }
    },

    openInvoice(url: string): Promise<InvoiceStatus> {
      return new Promise((resolve) => {
        if (!webApp.openInvoice) {
          resolve('failed')
          return
        }
        webApp.openInvoice(url, (status: InvoiceStatus) => resolve(status))
      })
    },

    openExternal(url: string) {
      if (webApp.openLink) {
        webApp.openLink(url, { try_instant_view: false })
      } else {
        window.open(url, '_blank', 'noopener')
      }
    },

    backButton: {
      show(onClick: () => void) {
        const button = webApp.BackButton
        if (!button) return
        button.offClick?.()
        button.onClick(onClick)
        button.show()
      },
      hide() {
        webApp.BackButton?.hide?.()
      },
    },

    theme() {
      return webApp.colorScheme === 'dark' ? 'dark' : 'light'
    },

    haptic(type) {
      const haptic = webApp.HapticFeedback
      if (!haptic) return
      if (type === 'success' || type === 'error') {
        haptic.notificationOccurred?.(type)
      } else {
        haptic.impactOccurred?.('light')
      }
    },
  }
}
